import React, { useEffect, useState } from "react";
import { Trophy, Medal, UsersFour, Spinner } from "@phosphor-icons/react";
import api from "@/lib/api";

const PODIUM_STYLES = {
  1: {
    border: "border-yellow-400/60",
    bg: "bg-gradient-to-b from-yellow-500/15 to-transparent",
    text: "text-yellow-300",
    label: "GOLD",
    height: "h-32",
  },
  2: {
    border: "border-zinc-400/50",
    bg: "bg-gradient-to-b from-zinc-400/10 to-transparent",
    text: "text-zinc-200",
    label: "SILVER",
    height: "h-24",
  },
  3: {
    border: "border-amber-700/60",
    bg: "bg-gradient-to-b from-amber-800/20 to-transparent",
    text: "text-amber-500",
    label: "BRONZE",
    height: "h-20",
  },
};

function PodiumBlock({ row }) {
  const style = PODIUM_STYLES[row.rank];
  return (
    <div
      data-testid={`podium-rank-${row.rank}`}
      className={`flex-1 rounded-sm border ${style.border} ${style.bg} p-3 flex flex-col justify-end ${style.height} min-w-0`}
    >
      <div className="flex items-center gap-1.5 mb-1">
        {row.rank === 1 ? (
          <Trophy size={14} weight="fill" className={style.text} />
        ) : (
          <Medal size={13} weight="fill" className={style.text} />
        )}
        <span className={`font-mono text-[9px] uppercase tracking-[0.18em] ${style.text}`}>{style.label}</span>
      </div>
      <div className="text-sm text-zinc-100 truncate font-medium">{row.name}</div>
      <div className="text-[11px] font-mono text-zinc-500 truncate">{row.email}</div>
      <div className="flex items-baseline gap-1 mt-1">
        <span className="font-display text-xl font-semibold text-emerald-400">+{row.days_earned}</span>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">days</span>
      </div>
    </div>
  );
}

export default function ReferralLeaderboard() {
  const [rows, setRows] = useState(null);

  useEffect(() => {
    api.get("/admin/referrals/leaderboard")
      .then((r) => setRows(r.data.leaderboard || []))
      .catch(() => setRows([]));
  }, []);

  if (rows === null) {
    return (
      <div
        data-testid="referral-leaderboard-loading"
        className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5 text-center text-zinc-500"
      >
        <Spinner size={16} className="animate-spin mx-auto" />
      </div>
    );
  }

  const podium = rows.slice(0, 3);
  const rest = rows.slice(3);

  return (
    <div
      data-testid="referral-leaderboard"
      className="bg-zinc-900/60 border border-zinc-800 rounded-sm p-5"
    >
      <div className="flex items-center gap-2 mb-4">
        <UsersFour size={16} weight="duotone" className="text-orange-500" />
        <div className="font-display font-medium text-zinc-100">Referral Leaderboard</div>
        <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">admin only</span>
      </div>

      {rows.length === 0 ? (
        <div
          data-testid="referral-leaderboard-empty"
          className="text-sm text-zinc-500 py-8 text-center border border-zinc-800/60 rounded-sm bg-zinc-950/40"
        >
          No referrals yet — be first to share your link.
        </div>
      ) : (
        <>
          {/* Podium (top 3, silver-gold-bronze ordering for visual balance) */}
          <div className="flex items-end gap-2 mb-4">
            {podium.find((r) => r.rank === 2) && <PodiumBlock row={podium.find((r) => r.rank === 2)} />}
            {podium.find((r) => r.rank === 1) && <PodiumBlock row={podium.find((r) => r.rank === 1)} />}
            {podium.find((r) => r.rank === 3) && <PodiumBlock row={podium.find((r) => r.rank === 3)} />}
          </div>

          {rest.length > 0 && (
            <div className="border border-zinc-800/60 rounded-sm bg-zinc-950/40 divide-y divide-zinc-800/60">
              {rest.map((r) => (
                <div
                  key={r.user_id}
                  data-testid={`leaderboard-row-${r.rank}`}
                  className="flex items-center justify-between gap-3 px-3 py-2"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs text-zinc-500 w-6 text-right">#{r.rank}</span>
                    <div className="min-w-0">
                      <div className="text-sm text-zinc-200 truncate">{r.name}</div>
                      <div className="text-[11px] font-mono text-zinc-500 truncate">{r.email}</div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-mono text-emerald-400">+{r.days_earned}d</div>
                    <div className="text-[10px] font-mono text-zinc-500">
                      {r.credited} paid · {r.pending} pending
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}
